import { useMemo } from 'react';
import { filterVisibleColumns } from '../util/filter-columns';
import { useAppSelector } from './useAppSelector';
import { Column } from '../enum/Column';
import { StatsTableColumn } from '../enum/TableColumn';

/**
 * useColumns
 *
 * Selector hook over Redux column state.
 * Does NOT manage local state or perform updates.
 *
 * What it provides:
 * - visibleColumns: only columns marked as visible
 * - lockedColumns: visible non-draggable columns (Geo, CCY)
 * - pinnedColumns / unpinnedColumns: applies to draggable columns
 *
 * Special case:
 * - lockedColumnsForSumBy excludes CCY
 *   because CCY is not a valid "Sum By Geo" level.
 *
 * Source of truth:
 * - Redux (state.configSlice.columnsByPage)
 */

export const useColumns = () => {
  const pageKey = useAppSelector((state) => state.configSlice.activePageKey);
  const columnsByPage = useAppSelector((state) => state.configSlice.columnsByPage);
  const currentColumns = useMemo(() => columnsByPage[pageKey] || [], [columnsByPage, pageKey]);
  const visibleColumns = useMemo(() => filterVisibleColumns(currentColumns), [currentColumns]);

  // Separate locked (Geo / CCY) and draggable columns
  const lockedColumns = visibleColumns.filter((col) => col.isLocked);
  const lockedColumnsForSumBy = currentColumns.filter((col) => col.isLocked && col.name !== StatsTableColumn.CCY);
  const draggableColumns = currentColumns.filter((col) => !col.isLocked);

  const pinnedColumns = draggableColumns.filter((column: Column) => column.isPinned);
  const unpinnedColumns = draggableColumns.filter((column: Column) => !column.isPinned);

  return {
    pinnedColumns,
    unpinnedColumns,
    visibleColumns,
    lockedColumns,
    lockedColumnsForSumBy,
    columns: currentColumns,
  };
};
